







































































































import { AxiosError } from 'axios';
import {
  IWorkflow,
  IWorkflowState,
  IWorkflowTransition,
  WorkflowState,
  WorkflowTransition
} from '@/models';

import { Edge, FlowEvents, MarkerType, Node } from '../workflow-canvas/types';
import {
  computed,
  defineComponent,
  nextTick,
  onMounted,
  PropType,
  ref
} from '@vue/composition-api';
import { groupsProvider } from '@vue-altoleap-libraries/vue-altoleap-accounts-lib';
import { useWorkFlow } from '../workflow-canvas/composables';
import { createNamespacedHelpers, useStore } from 'vuex-composition-helpers';
import { IRootState } from '@/store';
import { ISystemError } from '@/models/error/error.types';

import {
  IWorkflowStoreState,
  IWorkflowGetters,
  IWorkflowActions,
  IWorkflowMutations
} from '@/store/workflow';
import { cloneDeep, delay } from 'lodash';
import { SelectedDataTypes } from './types';
import { setErrorFromAxiosError } from '@/utils/helpers';

import ErrorAlert from '../common/ErrorAlert.vue';
import {
  DRAWER_WIDTH as TRANSITION_DRAWER_WIDTH,
  default as WorkflowTransitionDetailDrawer
} from './WorkflowTransitionDetailDrawer.vue';
import {
  DRAWER_WIDTH as STATE_DRAWER_WIDTH,
  default as WorkflowStateDetailDrawer
} from './WorkflowStateDetailDrawer.vue';
import WorkflowCanvas from '../workflow-canvas/container/WorkflowCanvas.vue';
import WorkflowStateFormDialog from './WorkflowStateFormDialog.vue';
import WorkflowPanel from '../workflow-canvas/components/Panel/Panel.vue';
import WorkflowPanelControls from '../workflow-canvas/components/WorkflowPanelControls.vue';
import {
  useWorkFlowGraph,
  useHorizontalLayout
} from '../workflow-canvas/composables/useWorkFlowGraph';
import { createGraphEdges, getEdgeId } from '../workflow-canvas/utils';

export enum Align {
  Center = 'center',
  Start = 'start',
  End = 'end'
}

export default defineComponent({
  name: 'WorkflowDetail',

  components: {
    ErrorAlert,
    WorkflowCanvas,
    WorkflowStateFormDialog,
    WorkflowStateDetailDrawer,
    WorkflowTransitionDetailDrawer,
    WorkflowPanelControls,
    WorkflowPanel
  },

  props: {
    workflow: {
      type: Object as PropType<IWorkflow>,
      required: true
    },
    disabled: {
      type: Boolean
    }
  },

  setup(props) {
    const {
      isValidConnection,
      defaultEdgeOptions,
      nodes: stateNodes,
      edges: stateEdges,

      fitView,
      findNode,
      findEdge,

      onConnect,
      addEdges,
      updateEdgeData,

      updateNodeData,
      removeEdges,
      addNodes
    } = useWorkFlow();

    // Should create of update a transition on the connection of a edge to a handle
    onConnect(async connection => {
      addEdges(connection);

      try {
        await createWorkflowTransition({
          workflowId: props.workflow.id!,
          transition: {
            name: 'Untitled',
            source_state: Number(connection.source),
            target_state: Number(connection.target)
          }
        });

        // update current edge connection with new id and data
        const lastCreatedTransition = [...getWorkflowTransitions.value].shift();

        //  Update the displayed edge data to match the newly created transition
        if (lastCreatedTransition) {
          updateEdgeData(getEdgeId(connection), lastCreatedTransition);

          await nextTick();

          if (findEdge(getEdgeId(connection))) {
            const clonedTransition = cloneDeep(
              parseTransitionIntoEdge(lastCreatedTransition)
            );

            const prevEdge = findEdge(getEdgeId(connection)) as Edge<
              IWorkflowTransition
            >;

            const newEdge: Edge<IWorkflowTransition> = {
              ...prevEdge,
              data: lastCreatedTransition,
              label: clonedTransition.label,
              id: clonedTransition.id,
              interactionWidth: 4,
              selectable: true,
              updatable: true,
              deletable: true,
              markerEnd: {
                type: MarkerType.Arrow,
                strokeWidth: 2,
                width: 20,
                height: 20
              }
            };

            if (newEdge) {
              const [validEdge] = createGraphEdges(
                [newEdge],
                isValidConnection.value,
                findNode,
                findEdge,
                defaultEdgeOptions.value,
                stateNodes.value,
                stateEdges.value
              );

              stateEdges.value.splice(
                stateEdges.value.findIndex(
                  edge => edge.id === getEdgeId(connection)
                ),
                1,
                validEdge
              );
            }
          }

          selectedState.value = null;
          isWorkflowStateDetailDrawerActive.value = false;
          await nextTick();
          selectedTransition.value = cloneDeep(lastCreatedTransition);
          isWorkflowTransitionDetailDrawerActive.value = true;
        }
      } catch (err) {
        console.log('FAILED TO CREATE CONNECTION', err);

        const createdEdgeId = getEdgeId(connection);
        removeEdges(createdEdgeId);
      }
    });

    const isLoading = ref(false);
    const isInitialized = ref(false);
    const error = ref<ISystemError | null>(null);

    function clearErrors() {
      error.value = null;
    }

    const store = useStore<IRootState>();

    const { useActions, useGetters } = createNamespacedHelpers<
      IWorkflowStoreState,
      IWorkflowGetters,
      IWorkflowActions,
      IWorkflowMutations
    >(store, 'workflow');

    const nodes = ref<Node<IWorkflowState>[]>([]);
    const edges = ref<Edge<IWorkflowTransition>[]>([]);

    const { getWorkflowStates, getWorkflowTransitions } = useGetters([
      'getWorkflowStates',
      'getWorkflowTransitions'
    ]);

    const { createWorkflowTransition } = useActions([
      'createWorkflowTransition'
    ]);

    const {
      parseStatesIntoNodes,
      parseTransitionIntoEdge,
      parseTransitionsIntoEdges
    } = useWorkFlowGraph();

    function onDeletedStateHandler(stateId: string) {
      const nodeIndex = nodes.value.findIndex(node => node.id === stateId);

      nodes.value.splice(nodeIndex, 1);

      selectedState.value = null;
    }

    function onDeletedTransitionHandler(transitionId: string) {
      const edgeIndex = edges.value.findIndex(edge => edge.id === transitionId);

      edges.value.splice(edgeIndex, 1);

      selectedTransition.value = null;
    }

    async function onCreatedStateHandler() {
      const lastCreatedState = [...getWorkflowStates.value].shift();

      if (lastCreatedState) {
        const parsedNodes = parseStatesIntoNodes([lastCreatedState]);
        nodes.value.push(...parsedNodes);
        addNodes(parsedNodes);
      }
    }

    function onUpdateStateHandler(state: IWorkflowState) {
      updateNodeData(state.id!.toString(), state);
    }

    const groupsStoreProvider = computed<any>(() => groupsProvider(store));

    onMounted(() => {
      try {
        nodes.value = parseStatesIntoNodes(getWorkflowStates.value);
        edges.value = parseTransitionsIntoEdges(getWorkflowTransitions.value);
        groupsStoreProvider.value.fetchPermissionGroups();
      } catch (err) {
        error.value = setErrorFromAxiosError(err as AxiosError);
      } finally {
        isInitialized.value = true;
      }
    });

    const { layout } = useHorizontalLayout();

    async function layoutGraph(nodeList?: Node<IWorkflowState>[]) {
      nodes.value = layout(nodeList ?? nodes.value, edges.value);

      // next tick seems to work for the initialization but the rendering of the nodes still take time
      await nextTick();

      delay(fitView, 200, { duration: 280, maxZoom: 1.5 });
    }

    const selectedType = ref<SelectedDataTypes>(SelectedDataTypes.State);
    const selectedState = ref<IWorkflowState | null>(null);
    const selectedTransition = ref<IWorkflowTransition | null>(null);

    // Event Listeners
    async function selectState(event: FlowEvents<IWorkflowState>['nodeClick']) {
      selectedTransition.value = null;
      isWorkflowTransitionDetailDrawerActive.value = false;

      await nextTick();
      selectedState.value = new WorkflowState(event.node.data);
      isWorkflowStateDetailDrawerActive.value = true;
    }

    async function selectTransition(
      event: FlowEvents<IWorkflowState>['edgeClick']
    ) {
      selectedState.value = null;
      isWorkflowStateDetailDrawerActive.value = false;

      await nextTick();
      selectedTransition.value = new WorkflowTransition(event.edge.data);
      isWorkflowTransitionDetailDrawerActive.value = true;
    }

    /** @internal */
    const isStateDrawerActive = ref(false);

    const isWorkflowStateDetailDrawerActive = computed({
      get() {
        return isStateDrawerActive.value;
      },

      set(value: boolean) {
        const DRAWER_OPEN_DELAY_MS = 300;

        if (value && !isStateDrawerActive.value) {
          delay(() => {
            isStateDrawerActive.value = true;
          }, DRAWER_OPEN_DELAY_MS);
        } else {
          isStateDrawerActive.value = value;
        }
      }
    });

    /** @internal */
    const isTransitionDrawerActive = ref(false);

    const isWorkflowTransitionDetailDrawerActive = computed({
      get() {
        return isTransitionDrawerActive.value;
      },

      set(value: boolean) {
        const DRAWER_OPEN_DELAY_MS = 300;

        if (value && !isTransitionDrawerActive.value) {
          delay(() => {
            isTransitionDrawerActive.value = true;
          }, DRAWER_OPEN_DELAY_MS);
        } else {
          isTransitionDrawerActive.value = value;
        }
      }
    });

    function fitNodeToView(
      event: FlowEvents<IWorkflowState>['nodeDoubleClick']
    ) {
      fitView({ nodes: [event.node.id], duration: 280 });
    }

    const workflowStateFormDialog = ref<
      InstanceType<typeof WorkflowStateFormDialog>
    >();

    return {
      // refs
      workflowStateFormDialog,

      isWorkflowStateDetailDrawerActive,
      isWorkflowTransitionDetailDrawerActive,
      isLoading,
      isInitialized,
      error,
      clearErrors,

      nodes,
      edges,
      layoutGraph,

      selectedState,
      selectedTransition,

      isWorkflowDetailDrawerActive: computed(
        () =>
          isWorkflowTransitionDetailDrawerActive.value ||
          isWorkflowStateDetailDrawerActive.value
      ),

      DRAWER_WIDTH: computed(() =>
        isWorkflowTransitionDetailDrawerActive.value
          ? TRANSITION_DRAWER_WIDTH
          : STATE_DRAWER_WIDTH
      ),

      // Events
      selectState,
      selectTransition,
      fitNodeToView,
      selectedType,
      SelectedDataTypes,

      onUpdateStateHandler,
      onCreatedStateHandler,
      onDeletedStateHandler,
      onDeletedTransitionHandler
    };
  }
});
